import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { StudentProgressService } from './student-progress.service';
import { FirstTutorialProgress } from '../models/student-progress.model';
import { StorageService } from '../../services/storage.service';
import { AuthUser } from '../../models/auth.model';

const OVERALL_STATUS_APPROVED = 'APPROVED';


@Injectable({ providedIn: 'root' })
export class TutorialGateService {
  private readonly studentProgressService = inject(StudentProgressService);
  private readonly storageService = inject(StorageService);

  private readonly _progress = signal<FirstTutorialProgress | null>(null);
  private readonly _loaded = signal<boolean>(false);
  private readonly _loadedForStudentCode = signal<string | null>(null);

  readonly progress = this._progress.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  private get isSocio(): boolean {
    const user = this.storageService.getUser<AuthUser>();
    return user?.range === 'Socio';
  }

  readonly isApproved = computed(() => this._progress()?.overallStatus === OVERALL_STATUS_APPROVED);

  readonly isLocked = computed(() => this._loaded() && !this.isApproved());

  readonly firstTutorialCourseId = computed(() => this._progress()?.courseId ?? null);

  check(studentCode: string): Observable<FirstTutorialProgress | null> {
    if (!this.isSocio) {
      const mockApproved = {
        overallStatus: OVERALL_STATUS_APPROVED,
        courseId: 0
      } as unknown as FirstTutorialProgress;
      this.applyResult(studentCode, mockApproved);
      return of(mockApproved);
    }

    return this.studentProgressService.getFirstTutorialProgress(studentCode).pipe(
      map((res) => res.data ?? null),
      tap((data) => this.applyResult(studentCode, data)),
      catchError(() => {
        this.applyResult(studentCode, null);
        return of(null);
      }),
    );
  }

  ensureLoaded$(studentCode: string | null | undefined): Observable<FirstTutorialProgress | null> {
    if (!studentCode) return of(null);
    if (this._loadedForStudentCode() === studentCode) return of(this._progress());
    return this.check(studentCode);
  }

  load(studentCode: string | null | undefined): void {
    if (!studentCode) return;
    this.ensureLoaded$(studentCode).subscribe();
  }

  reset(): void {
    this._progress.set(null);
    this._loaded.set(false);
    this._loadedForStudentCode.set(null);
  }

  private applyResult(studentCode: string, data: FirstTutorialProgress | null): void {
    this._progress.set(data);
    this._loaded.set(true);
    this._loadedForStudentCode.set(studentCode);
  }
}
